import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";
import {
  fetchPersonalizationFonts,
  createPersonalizationFont,
  updatePersonalizationFont,
  deletePersonalizationFont,
} from "../../api/DesignAPIs";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { CatalogShell, ConfirmDialog, TableSkeleton, EmptyState, Switch } from "./catalogUi";

const emptyForm = {
  name: "",
  supportsArabic: true,
  supportsEnglish: true,
  file: null,
};

const fontFileUrl = (item) => {
  const path = item?.fileUrl || "";
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_BASE_URL_V2}${path.startsWith("/") ? "" : "/"}${path}`;
};

const PersonalizationFontsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canManage = isAdmin(authState.role);
  const [formMode, setFormMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { data = [], isLoading, error, refetch } = useQuery({
    queryKey: ["personalizationFonts", "all"],
    queryFn: () => fetchPersonalizationFonts({ all: true }),
    enabled: canManage,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return undefined;
    const faces = [];
    data.forEach((item) => {
      const href = fontFileUrl(item);
      const family = item.webFamily || item.cssFamily;
      if (!href || !family) return;
      const face = new FontFace(family, `url("${href}")${item.format ? ` format("${item.format}")` : ""}`);
      const promise = face.load().then((loaded) => {
        document.fonts.add(loaded);
        return loaded;
      }).catch(() => null);
      faces.push(promise);
    });
    return undefined;
  }, [data]);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  const openCreate = () => {
    setFormMode("create");
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item) => {
    setFormMode("edit");
    setEditing(item);
    setForm({
      name: item.name,
      supportsArabic: item.supportsArabic !== false,
      supportsEnglish: item.supportsEnglish !== false,
      file: null,
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("اسم الخط مطلوب");
      return;
    }
    if (formMode === "create" && !form.file) {
      toast.error("ارفع ملف TTF أو OTF");
      return;
    }
    setSaving(true);
    try {
      if (formMode === "create") {
        await createPersonalizationFont(form);
        toast.success("تم رفع الخط");
      } else {
        await updatePersonalizationFont(editing.id, form);
        toast.success("تم حفظ الخط");
      }
      closeForm();
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await updatePersonalizationFont(item.id, { isActive: !item.isActive });
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePersonalizationFont(deleteTarget.id);
      toast.success("تم حذف الخط");
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const sorted = useMemo(
    () => [...data].sort((a, b) => Number(a.isBuiltIn) - Number(b.isBuiltIn) || a.name.localeCompare(b.name, "ar")),
    [data]
  );

  return (
    <CatalogShell
      title="خطوط التخصيص"
      subtitle="ارفع الخط مرة واحدة واستخدمه في أي تصميم من غير build"
      actions={
        canManage && (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl font-bold"
          >
            <Plus size={18} />
            رفع خط
          </button>
        )
      }
    >
      <ToastContainer position="top-center" rtl />
      {error && <p className="text-red-600 mb-4">{error.message}</p>}
      {isLoading ? (
        <TableSkeleton />
      ) : sorted.length === 0 ? (
        <EmptyState text="لا توجد خطوط بعد" />
      ) : (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <table className="w-full text-right">
            <thead className="bg-indigo-50 text-indigo-900">
              <tr>
                <th className="p-3">الخط</th>
                <th className="p-3">العينة</th>
                <th className="p-3">اللغات</th>
                <th className="p-3">الحالة</th>
                <th className="p-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">
                    <div className="font-bold">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.fileName}</div>
                    {item.isBuiltIn && <span className="text-xs text-indigo-600">أساسي</span>}
                  </td>
                  <td className="p-3">
                    <span style={{ fontFamily: `"${item.webFamily || item.cssFamily}", sans-serif` }}>محمد Mohamed</span>
                  </td>
                  <td className="p-3 text-sm">
                    {item.supportsArabic ? "عربي " : ""}
                    {item.supportsEnglish ? "إنجليزي" : ""}
                  </td>
                  <td className="p-3">
                    <Switch checked={item.isActive} onChange={() => toggleActive(item)} />
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => openEdit(item)} className="p-2 rounded-lg bg-blue-50 text-blue-700">
                        <Pencil size={16} />
                      </button>
                      {!item.isBuiltIn && (
                        <button type="button" onClick={() => setDeleteTarget(item)} className="p-2 rounded-lg bg-red-50 text-red-700">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formMode && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={handleSave} className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-xl font-bold">{formMode === "create" ? "رفع خط جديد" : "تعديل الخط"}</h3>
            <label className="block">
              <span className="font-medium">اسم الخط</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 p-3 border-2 rounded-xl"
                required
              />
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.supportsArabic}
                  onChange={(e) => setForm({ ...form, supportsArabic: e.target.checked })}
                />
                يدعم العربي
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.supportsEnglish}
                  onChange={(e) => setForm({ ...form, supportsEnglish: e.target.checked })}
                />
                يدعم الإنجليزي
              </label>
            </div>
            <label className="block">
              <span className="font-medium">{formMode === "create" ? "ملف الخط (TTF / OTF)" : "استبدال الملف (اختياري)"}</span>
              <input
                type="file"
                accept=".ttf,.otf,font/ttf,font/otf"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="w-full mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">لو الخط OTF من نوع CFF حوّله إلى TTF قبل الرفع.</p>
            </label>
            {editing && (
              <p className="text-sm" style={{ fontFamily: `"${editing.webFamily || editing.cssFamily}", sans-serif` }}>
                محمد Mohamed
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={closeForm} className="px-4 py-2 rounded-xl bg-gray-200">
                إلغاء
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold">
                {saving ? "جاري الحفظ..." : "حفظ"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="حذف الخط؟"
        message={deleteTarget ? `سيتم حذف ${deleteTarget.name} إذا لم يكن مستخدمًا في أي تصميم.` : ""}
        confirmLabel="حذف"
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </CatalogShell>
  );
};

export default PersonalizationFontsPage;
